import React, { useCallback, useState } from 'react';
import { ERROR_MESSAGES, toUserMessage } from '../../utils/userErrors';
import AppLoader from '../../components/modules/AppLoader';
import apiService from '../../utils/apiService';
import { parseUserProfile } from '../../utils/userProfile';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import { generateQrDataUrl, pickProfileField } from './qrHelpers';
import QrStudio from './QrStudio';
import '../../styles/module-shared.css';
import './qr-code.css';

const ClientQr = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [affiliateCode, setAffiliateCode] = useState('');
  const [clientUrl, setClientUrl] = useState('');
  const [qrSource, setQrSource] = useState('url');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrGenerating, setQrGenerating] = useState(false);
  const [qrError, setQrError] = useState('');
  const [qrLabel, setQrLabel] = useState('');

  const fetchProfile = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getUserProfile();
      if (cancelled()) return;
      if (!res.success || !res.data) {
        setError(res.error || 'Failed to load profile');
        return;
      }
      const profile = parseUserProfile(res.data);
      setAffiliateCode(pickProfileField(profile, 'affiliateCode'));
      setClientUrl(pickProfileField(profile, 'partnerUrl'));
    } catch (err) {
      if (!cancelled()) setError(toUserMessage(err, ERROR_MESSAGES.load));
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey('client-qr-profile', fetchProfile, true);

  const handleSourceChange = (source) => {
    setQrSource(source);
    setQrDataUrl('');
    setQrLabel('');
    setQrError('');
  };

  const handleGenerateQr = async () => {
    const payload = qrSource === 'code' ? affiliateCode : clientUrl;
    if (!payload) {
      setQrError(
        qrSource === 'code'
          ? 'Partner code is not available.'
          : 'Client URL is not available.'
      );
      setQrDataUrl('');
      setQrLabel('');
      return;
    }

    setQrGenerating(true);
    setQrError('');
    try {
      const dataUrl = await generateQrDataUrl(payload);
      setQrDataUrl(dataUrl);
      setQrLabel(payload);
    } catch {
      setQrDataUrl('');
      setQrLabel('');
      setQrError('Failed to generate QR code. Please try again.');
    } finally {
      setQrGenerating(false);
    }
  };

  if (loading) return <AppLoader />;

  return (
    <QrStudio
      title="Client QR"
      subtitle="Generate a premium QR from your partner code or client portal URL"
      codeLabel="Partner Code"
      urlLabel="Client URL"
      codeValue={affiliateCode}
      urlValue={clientUrl}
      qrSource={qrSource}
      onSourceChange={handleSourceChange}
      onGenerate={handleGenerateQr}
      qrGenerating={qrGenerating}
      qrError={qrError}
      qrDataUrl={qrDataUrl}
      qrLabel={qrLabel}
      downloadName={`client-qr-${qrSource}.png`}
      error={error}
    />
  );
};

export default ClientQr;
